import { portalGet } from '@/features/portal/utils/portalRequest';
import { apiGet } from '@/shared/utils/requestBuilder';

import type { OrigemDocumentoEncerramento } from '../../types/encerramento.types';
import type { SentencaArbitralDados } from '../../types/sentencaArbitral.types';
import { sentencaArbitralDtoSchema } from '../../types/sentencaArbitralDto.schema';
import { mapSentencaArbitral } from '../../utils/sentencaArbitralMapper';

const TIMEOUT_MS = 10000;

// Cinco endereços, um por papel, MESMO documento (rn4). O `:id` da superfície do notificado é a
// parteArbitral (o portal inteiro navega por ela); nas demais é o processo. As duas superfícies
// de portal autenticam pela sessão do portal (portalGet), as outras três pelo JWT do workspace.
const PATH_POR_ORIGEM: Record<OrigemDocumentoEncerramento, (id: string) => string> = {
  secretaria: (id) => `/admin/arbitral/processos/${id}/sentenca`,
  parte: (id) => `/arbitral/processos/${id}/sentenca`,
  arbitro: (id) => `/arbitral/painel/processos/${id}/sentenca`,
  advogado: (id) => `/arbitral/portal/advogados/processos/${id}/sentenca`,
  notificado: (id) => `/arbitral/portal/citacoes/${id}/sentenca`,
};

const USA_SESSAO_DO_PORTAL: Record<OrigemDocumentoEncerramento, boolean> = {
  secretaria: false,
  parte: false,
  arbitro: false,
  advogado: true,
  notificado: true,
};

export class SentencaSemPermissaoError extends Error {
  constructor() {
    super('Você não tem permissão para visualizar esta sentença.');
    this.name = 'SentencaSemPermissaoError';
  }
}

// 404 = a sentença ainda não foi publicada (rn1) — estado Bloqueado, nunca documento em branco.
export class SentencaNaoPublicadaError extends Error {
  constructor() {
    super('A sentença ainda não foi proferida neste processo.');
    this.name = 'SentencaNaoPublicadaError';
  }
}

const ERRO_GENERICO = 'Erro ao carregar o documento da sentença';

export const sentencaArbitralService = {
  getSentenca: async (
    origem: OrigemDocumentoEncerramento,
    id: string,
  ): Promise<SentencaArbitralDados> => {
    const path = PATH_POR_ORIGEM[origem](id);
    let response: Response;
    try {
      response = USA_SESSAO_DO_PORTAL[origem]
        ? await portalGet(path, undefined, false, undefined, TIMEOUT_MS)
        : await apiGet(path, undefined, false, AbortSignal.timeout(TIMEOUT_MS));
    } catch {
      throw new Error(ERRO_GENERICO);
    }

    if (response.status === 403) throw new SentencaSemPermissaoError();
    if (response.status === 404) throw new SentencaNaoPublicadaError();
    if (!response.ok) throw new Error(ERRO_GENERICO);

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new Error(ERRO_GENERICO);
    }
    if (typeof body !== 'object' || body === null || !('data' in body)) {
      throw new Error(ERRO_GENERICO);
    }
    const dto = sentencaArbitralDtoSchema.safeParse(body.data);
    if (!dto.success) throw new Error(ERRO_GENERICO);
    return mapSentencaArbitral(dto.data);
  },
};
